import './TableColumnDd.css'

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'
import { UseDropdownToggleProps } from '@restart/ui/DropdownToggle'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { TableAdapter } from '@makyoapp/makyo-common'
import { ArrowLeft16, ArrowRight16, ChevronDown, Search16 } from '@makyoapp/makyo-icon/icon'
import { CloseCircle, DD, DDItem, useTheme } from '@makyoapp/makyo-ui'

import { GroupedTable } from '@/core/db'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useGetTablesGroup } from '@/hooks/useGetTablesGroup'
import { getTableColumnsWithRowId, isEmpty } from '@/shared/utils'
import { ColumnTypeIcon } from '@/components/ColumnTypeIcon'
import { RenderTableIcon, TableIconMap } from '@/components/RenderTableIcon'

interface TableColumnDdValue {
  table: string
  column: string
}

interface TableColumnDdToggleProps {
  id?: string
  active: boolean
  toggleProps: UseDropdownToggleProps
  value: TableColumnDdValue
  options: TableAdapter[]
  selectLabel?: React.ReactNode
}

function TableColumnDdToggle(props: TableColumnDdToggleProps) {
  const table = props.options.find((tb) => tb.id === props.value.table)
  const tableColumns = getTableColumnsWithRowId(table?.columns || [])

  const column = tableColumns.find((c) => c.id === props.value.column)

  const { state: themeState } = useTheme()
  const isRtl = themeState.dir === 'rtl'

  return (
    <div className='sdd-toggle sdd-toggle-normal txt w-full'>
      {props.value.table && props.value.column ? (
        <div className='flex-c w-full txt'>
          <div className='flex-c txt'>
            <RenderTableIcon table={table} />
            <div className='txt'>{table?.name}</div>
          </div>
          <div className='px-2'>{!isRtl ? <ArrowRight16 /> : <ArrowLeft16 />}</div>
          <div className='flex-c txt'>
            <ColumnTypeIcon type={column?.type || ColumnTypes.Text} className='shrink-0' />
            <div className='txt'>{column?.name}</div>
          </div>
        </div>
      ) : (
        <div className='flex-c w-full'>
          <RenderTableIcon isPlaceholder />
          <div className='text-light'>{props.selectLabel || <Trans>Select Source</Trans>}</div>
        </div>
      )}
      <ChevronDown className='shrink-0' />
    </div>
  )
}

interface TableColumnDdProps {
  id?: string
  value: TableColumnDdValue
  options: TableAdapter[]
  onChange: (tableId: string, colId: string) => void
  showPlaceholder?: boolean
  excludeTypes?: ColumnTypes[]
  withSearch?: boolean
}

export function TableColumnDd({
  id,
  value,
  options,
  onChange,
  showPlaceholder,
  excludeTypes,
  withSearch,
}: TableColumnDdProps) {
  const [show, setShowRaw] = useBoolean(false)
  const [showCol, setShowCol] = useState<GroupedTable | undefined>(undefined)
  const [disableAnim, setDisableAnim] = useBoolean(true)
  const [openCol, setOpenCol] = useBoolean(false)
  const [tableSearch, setTableSearch] = useState('')
  const [columnSearch, setColumnSearch] = useState('')
  const activeTableRef = useRef<HTMLDivElement>(null)
  const activeColumnRef = useRef<HTMLDivElement>(null)

  const { tables: tableAdapters } = useDbClientContext()
  const [groupedTables] = useGetTablesGroup()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const setShow = useCallback(
    (next: boolean) => {
      if (!next) {
        setShowCol(undefined)
        setOpenCol(false)
      } else {
        timeoutRef.current = setTimeout(() => {
          activeTableRef.current?.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
          })
        }, 100)
      }
      setShowRaw(next)
    },
    [setShowRaw, setShowCol, setOpenCol]
  )

  const allTables = useMemo(() => {
    return isEmpty(options) ? tableAdapters : options
  }, [tableAdapters, options])

  const tableOptions: GroupedTable[] = useMemo(() => {
    const options = groupedTables.filter((item) => allTables.some((t) => t.id === item.id))
    return showPlaceholder ? [{ id: '', name: 'Select Table', type: 'source' }, ...options] : options
  }, [groupedTables, showPlaceholder, allTables])

  const filteredTables = useMemo(() => {
    if (!tableSearch) return tableOptions

    const parents = tableOptions.filter(
      (item) => !item.parent && item.name.toLowerCase().includes(tableSearch.toLowerCase())
    )
    const children = tableOptions.filter(
      (item) => item.parent && item.name.toLowerCase().includes(tableSearch.toLowerCase())
    )
    const parentsOfChildren = tableOptions.filter((item) => children.some((c) => c.parent === item.id))

    return [...new Set([...parents, ...parentsOfChildren, ...children])]
  }, [tableOptions, tableSearch])

  const selectedTable = allTables.find((item) => item.id === showCol?.id)

  const tableColumns =
    Array.isArray(excludeTypes) && excludeTypes.length > 0
      ? selectedTable?.columns?.filter((cl) => !excludeTypes?.includes(cl.type))
      : selectedTable?.columns?.filter((cl) => !excludeTypes?.includes(cl.type))

  // Filter columns based on search
  const columns = getTableColumnsWithRowId(tableColumns || []).filter(
    (column) => !columnSearch || column.name.toLowerCase().includes(columnSearch.toLowerCase())
  )

  useLayoutEffect(() => {
    if (!openCol && showCol !== undefined) {
      timeoutRef.current = setTimeout(() => {
        setShowCol(undefined)
      }, 100)
    } else if (openCol) {
      timeoutRef.current = setTimeout(() => {
        activeColumnRef.current?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        })
      }, 350)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [openCol, showCol, setShowCol])

  return (
    <DD
      id={id}
      onToggle={(e, meta) => {
        if (meta.source !== 'select') {
          setDisableAnim(true)
          setShow(!show)
        } else {
          setDisableAnim(false)
        }
      }}
      onSelect={(eventKey, e) => {
        e.preventDefault()
      }}
      show={show}
      className='w-full txt'
      toggleClassName='w-full txt'
      toggle={(active, toggleProps) => {
        return (
          <TableColumnDdToggle
            options={options}
            active={active}
            toggleProps={toggleProps}
            value={value}
            selectLabel={showPlaceholder ? <Trans>Select Table</Trans> : undefined}
          />
        )
      }}
      menuClassName='sdd-menu-subc z-501 overflow-x-hidden mk-ag-sdd mk-tbl-dd'
      menuNoPad
      menuWrapperClassName='w-[240px]'
      portalMenu
    >
      {/* Table List */}
      <div
        className={clsx('mk-tbl-dd-tbl', openCol && 'open', !openCol && !disableAnim && 'close', openCol && 'hidden')}
      >
        {withSearch && (
          <div className='sdd-search-wrap'>
            <Search16 className='sdd-search-icon' />
            <input
              type='text'
              className='p-2'
              value={tableSearch}
              placeholder='Search tables...'
              onChange={(e) => {
                setTableSearch?.(e.currentTarget.value)
              }}
            />
            <button
              type='button'
              className={clsx('sdd-search-remove', tableSearch ? 'visible' : 'invisible')}
              onClick={() => {
                setTableSearch?.('')
              }}
            >
              <CloseCircle />
            </button>
          </div>
        )}

        {filteredTables.map((item, index) => {
          const isActive = value.table === item.id
          return (
            <div
              ref={isActive ? activeTableRef : null}
              key={`${item.id}-${index}`}
              className={clsx(
                'flex cursor-pointer items-center rounded-sm px-4 py-2 hover:bg-zinc-200 dark:hover:bg-slate-700',
                'truncate select-none',
                item.parent && 'pl-8',
                isActive ? 'bg-zinc-100 dark:bg-slate-600' : '',
                item.id === '' && 'text-light'
              )}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setOpenCol(true)
                setShowCol(item)
              }}
            >
              <div className='flex-1 flex items-center gap-x-2'>
                {TableIconMap[item.type]}
                <span className='flex-1 text-left truncate'>{item.name}</span>
                {!!item.childrenCount && item.childrenCount > 0 && (
                  <span className='text-m-sm text-light'>{item.childrenCount} views</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Column List */}
      <div className={clsx('mk-tbl-dd-col', openCol && 'col-open', !openCol && !disableAnim && 'col-close')}>
        <DDItem className='sdd-item mk-tbl-item mk-tbl-item-back' onClick={() => setOpenCol(false)}>
          <ArrowLeft16 />
          <div>
            <Trans>Back</Trans>
          </div>
        </DDItem>
        {withSearch && (
          <div className='sdd-search-wrap'>
            <Search16 className='sdd-search-icon' />
            <input
              type='text'
              className='sdd-search-input'
              value={columnSearch}
              placeholder='Search columns...'
              onChange={(e) => {
                setColumnSearch?.(e.currentTarget.value)
              }}
            />
            <button
              type='button'
              className={clsx('sdd-search-remove', columnSearch ? 'visible' : 'invisible')}
              onClick={() => {
                setColumnSearch?.('')
              }}
            >
              <CloseCircle />
            </button>
          </div>
        )}
        {columns?.map((opt) => {
          const isActive = opt.id === value.column
          return (
            <div key={opt.id} ref={isActive ? activeColumnRef : null}>
              <DDItem
                className={clsx('sdd-item mk-tbl-item', isActive && 'sdd-item-selected')}
                onClick={() => {
                  if (showCol) {
                    onChange(showCol.id, opt.id)
                    setShow(false)
                    setDisableAnim(true)
                    setOpenCol(false)
                    setShowCol(undefined)
                  }
                }}
              >
                <div className={clsx('mk-tbl-col-icon')}>
                  <ColumnTypeIcon type={opt.type} />
                </div>
                <div className='txt'>{opt.name}</div>
              </DDItem>
            </div>
          )
        })}
      </div>
    </DD>
  )
}
